import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import TagMultiSelect from '../../../components/crm/TagMultiSelect';
import InfoTooltip from '../../../components/ui/InfoTooltip';
import { campaignIdsForProspect } from '../../../utils/campaignProspects';
import ProspectCustomFieldsEditor from './ProspectCustomFieldsEditor';
import {
  customFieldRowsFromDefinitions,
  normalizeCustomFieldsForApi,
} from '../../../utils/prospectCustomFields';

const FIELD_HINTS = {
  fullName: 'Ex. : Marie Dupont',
  company: 'Ex. : TechStart SAS',
  position: 'Ex. : Directrice commerciale',
  phone: 'Ex. : +33 6 12 34 56 78',
  mobilePhone: 'Ex. : +33 7 12 34 56 78',
  email: 'Ex. : contact@entreprise.fr',
  callbackAt: 'Date et heure du prochain rappel à planifier.',
  address: 'Ex. : 10 rue de Paris',
  postalCode: 'Ex. : 75001',
  city: 'Ex. : Paris',
  website: 'Ex. : https://www.entreprise.fr',
  ape: 'Format XX.XXZ : le point est obligatoire. Ex. : 62.01Z.',
  siret: 'Ex. : 12345678901234 (14 chiffres)',
  latitude: 'Ex. : 48.8566 (décimal, WGS84)',
  longitude: 'Ex. : 2.3522 (décimal, WGS84)',
  notes: 'Compte rendu, contexte, prochaines actions…',
};

const ContactForm = ({
  onClose,
  onSubmit,
  initialData,
  campaigns = [],
  customFieldDefinitions = [],
  canManageDefinitions = false,
  onAddCustomFieldDefinition,
  addingCustomFieldDefinition = false,
}) => {
  const [formData, setFormData] = useState({
    fullName: initialData?.fullName || '',
    company: initialData?.company || '',
    position: initialData?.position || '',
    phone: initialData?.phone || '',
    mobilePhone: initialData?.mobilePhone || '',
    email: initialData?.email || '',
    status: initialData?.status || 'prospect',
    priority: initialData?.priority || 'low',
    notes: initialData?.notes || '',
    callbackAt: initialData?.callbackAt || '',
    address: initialData?.address || '',
    postalCode: initialData?.postalCode || '',
    city: initialData?.city || '',
    website: initialData?.website || '',
    ape: initialData?.ape || '',
    siret: initialData?.siret || '',
    latitude: initialData?.latitude ?? '',
    longitude: initialData?.longitude ?? '',
    tags: initialData?.tags || [],
    customFieldRows: [],
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      customFieldRows: customFieldRowsFromDefinitions(
        customFieldDefinitions,
        initialData?.customFields ?? prev.customFieldRows,
      ),
    }));
  }, [customFieldDefinitions, initialData?.id]);

  const [errors, setErrors] = useState({});
  const [campaignIds, setCampaignIds] = useState(() =>
    campaignIdsForProspect(initialData?.tags || [], campaigns),
  );

  const toggleCampaign = (cid) => {
    const campaign = campaigns.find((c) => c.id === cid);
    if (!campaign) return;

    setCampaignIds((prev) => {
      const adding = !prev.includes(cid);
      const next = adding ? [...prev, cid] : prev.filter((id) => id !== cid);

      setFormData((f) => {
        let tags = [...(f.tags || [])];
        if (adding) {
          tags = [...new Set([...tags, ...(campaign.tags || [])])];
        } else {
          const stillNeeded = new Set(
            campaigns
              .filter((c) => next.includes(c.id))
              .flatMap((c) => c.tags || []),
          );
          tags = tags.filter(
            (t) => stillNeeded.has(t) || !(campaign.tags || []).includes(t),
          );
        }
        return { ...f, tags };
      });

      return next;
    });
  };

  const statusOptions = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'qualifie', label: 'Qualifié' },
    { value: 'proposition', label: 'Proposition' },
    { value: 'negociation', label: 'Négociation' },
    { value: 'conclu', label: 'Conclu' },
    { value: 'non_interesse', label: 'Non intéressé' }
  ];

  const priorityOptions = [
    { value: 'high', label: 'Haute' },
    { value: 'medium', label: 'Moyenne' },
    { value: 'low', label: 'Basse' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fullName = formData?.fullName?.trim() || '';
    const company = formData?.company?.trim() || '';
    const phone = formData?.phone?.trim() || '';
    const email = formData?.email?.trim() || '';

    if (!fullName && !company && !phone && !email) {
      newErrors.fullName =
        'Renseignez au moins un nom, une entreprise, un téléphone ou un e-mail';
    }

    if (phone && !/^\+?[0-9\s.-]+$/?.test(phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (formData?.mobilePhone?.trim() && !/^\+?[0-9\s.-]+$/?.test(formData?.mobilePhone)) {
      newErrors.mobilePhone = 'Format de mobile invalide';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      const { customFieldRows, ...rest } = formData;
      onSubmit({
        ...rest,
        customFields: normalizeCustomFieldsForApi(customFieldRows),
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {initialData ? 'Modifier le prospect' : 'Ajouter un prospect'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {initialData
                ? 'Mettez à jour les informations du prospect'
                : 'Remplissez les informations du nouveau contact'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nom complet"
                labelTooltip={FIELD_HINTS.fullName}
                value={formData?.fullName}
                onChange={(e) => handleChange('fullName', e?.target?.value)}
                error={errors?.fullName}
              />

              <Input
                label="Entreprise (optionnel)"
                labelTooltip={FIELD_HINTS.company}
                value={formData?.company}
                onChange={(e) => handleChange('company', e?.target?.value)}
                error={errors?.company}
              />
            </div>

            <Input
              label="Poste"
              labelTooltip={FIELD_HINTS.position}
              value={formData?.position}
              onChange={(e) => handleChange('position', e?.target?.value)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Téléphone (optionnel)"
                type="tel"
                labelTooltip={FIELD_HINTS.phone}
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                error={errors?.phone}
              />

              <Input
                label="Mobile (optionnel)"
                type="tel"
                labelTooltip={FIELD_HINTS.mobilePhone}
                value={formData?.mobilePhone}
                onChange={(e) => handleChange('mobilePhone', e?.target?.value)}
                error={errors?.mobilePhone}
              />
            </div>

            <Input
              label="Email (optionnel)"
              type="email"
              labelTooltip={FIELD_HINTS.email}
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
            />

            {campaigns.length > 0 ? (
              <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                <p className="text-sm font-medium text-foreground">Campagnes</p>
                <p className="text-xs text-muted-foreground">
                  Cochez une ou plusieurs campagnes : les tags requis sont ajoutés au prospect
                  (même règle que le filtre annuaire et l&apos;emailing).
                </p>
                <div className="flex flex-col gap-2 max-h-36 overflow-y-auto">
                  {campaigns.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-start gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-border"
                        checked={campaignIds.includes(c.id)}
                        onChange={() => toggleCampaign(c.id)}
                      />
                      <span>
                        <span className="font-medium text-foreground">{c.name}</span>
                        {Array.isArray(c.tags) && c.tags.length > 0 ? (
                          <span className="block text-xs text-muted-foreground">
                            Tags requis : {c.tags.join(', ')}
                          </span>
                        ) : (
                          <span className="block text-xs text-amber-700 dark:text-amber-400">
                            Aucun tag défini sur cette campagne
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <TagMultiSelect
              label="Tags"
              value={formData.tags || []}
              onChange={(tags) => handleChange('tags', tags)}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Statut"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
                placeholder="Sélectionner"
              />

              <Select
                label="Priorité"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleChange('priority', value)}
                placeholder="Sélectionner"
              />
              <Input
                label="Date de rappel (optionnel)"
                type="datetime-local"
                labelTooltip={FIELD_HINTS.callbackAt}
                value={formData?.callbackAt}
                onChange={(e) => handleChange('callbackAt', e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Adresse (optionnel)"
                labelTooltip={FIELD_HINTS.address}
                value={formData?.address}
                onChange={(e) => handleChange('address', e?.target?.value)}
              />
              <Input
                label="Code postal (optionnel)"
                labelTooltip={FIELD_HINTS.postalCode}
                value={formData?.postalCode}
                onChange={(e) => handleChange('postalCode', e?.target?.value)}
              />
              <Input
                label="Ville (optionnel)"
                labelTooltip={FIELD_HINTS.city}
                value={formData?.city}
                onChange={(e) => handleChange('city', e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Site web (optionnel)"
                labelTooltip={FIELD_HINTS.website}
                value={formData?.website}
                onChange={(e) => handleChange('website', e?.target?.value)}
              />
              <Input
                label="Code APE (optionnel)"
                labelTooltip={FIELD_HINTS.ape}
                value={formData?.ape}
                onChange={(e) => handleChange('ape', e?.target?.value)}
              />
              <Input
                label="SIRET (optionnel)"
                labelTooltip={FIELD_HINTS.siret}
                value={formData?.siret}
                onChange={(e) => handleChange('siret', e?.target?.value)}
              />
              <Input
                label="Latitude (optionnel)"
                type="number"
                step="0.000001"
                labelTooltip={FIELD_HINTS.latitude}
                value={formData?.latitude}
                onChange={(e) => handleChange('latitude', e?.target?.value)}
              />
              <Input
                label="Longitude (optionnel)"
                type="number"
                step="0.000001"
                labelTooltip={FIELD_HINTS.longitude}
                value={formData?.longitude}
                onChange={(e) => handleChange('longitude', e?.target?.value)}
              />
            </div>

            <ProspectCustomFieldsEditor
              rows={formData.customFieldRows}
              onChange={(customFieldRows) =>
                setFormData((prev) => ({ ...prev, customFieldRows }))
              }
              canManageDefinitions={canManageDefinitions}
              onAddDefinition={onAddCustomFieldDefinition}
              addingDefinition={addingCustomFieldDefinition}
            />

            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-2">
                Notes
                <InfoTooltip text={FIELD_HINTS.notes} />
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                rows={4}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Annuler
            </Button>
            <Button
              type="submit"
              iconName="Check"
              iconPosition="left"
              fullWidth
            >
              {initialData ? 'Enregistrer les modifications' : 'Ajouter le prospect'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
